export const TRIAL_FREE_CHAT_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export const TRIAL_FREE_CHAT_BLOCKED_MESSAGE = "Please subscribe to continue chatting.";

export const TRIAL_DISCUSS_MODE_BLOCKED_MESSAGE =
  "Your free Chat trial has ended. You can switch to Allow Edits mode to keep chatting using your trial credits, or subscribe now for unlimited Chat.";

/** One day past the free-chat window; used by admin tooling to simulate expiry. */
export const TRIAL_FREE_CHAT_EXPIRED_SIGNED_UP_AT_OFFSET_MS =
  TRIAL_FREE_CHAT_DURATION_MS + 24 * 60 * 60 * 1000;

export type TrialFreeChatSubscriptionView = {
  activeTrial?: (() => boolean) | boolean;
  signedUpAt?: Date | { toDate?: () => Date; _seconds?: number } | string | number | null;
  quota?: number | string | null;
};

export type PaidChatCreditSubscriptionView = {
  activeSubscription?: (() => boolean) | boolean;
  allowAIFunctions?: (() => boolean) | boolean;
  totalQuota?: number | string | null;
};

const AGENTIC_CHAT_MESSAGE_PATH = "/api/ai/agenticChat/message";

export function isFreeAgenticChatOnlyMessageRequest(
  method: unknown,
  requestedUrl: unknown,
  body: unknown,
): boolean {
  if (method !== "POST") return false;
  if (requestedUrl !== AGENTIC_CHAT_MESSAGE_PATH && requestedUrl !== `${AGENTIC_CHAT_MESSAGE_PATH}/replace`) return false;
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  return (body as { mode?: unknown }).mode === "chat";
}

export function isPaidChatOnlyAfterCreditsExhausted(
  sub: PaidChatCreditSubscriptionView | null | undefined,
  mode: "chat" | "write",
): boolean {
  if (mode !== "chat" || !sub) return false;
  const activeSubscription =
    typeof sub.activeSubscription === "function"
      ? sub.activeSubscription.call(sub)
      : Boolean(sub.activeSubscription);
  if (!activeSubscription) return false;
  const paidAiAccess =
    typeof sub.allowAIFunctions === "function"
      ? sub.allowAIFunctions.call(sub)
      : Boolean(sub.allowAIFunctions);
  if (paidAiAccess) return false;

  const rawQuota = sub.totalQuota;
  const totalQuota =
    typeof rawQuota === "number"
      ? rawQuota
      : typeof rawQuota === "string" && rawQuota.trim() !== ""
        ? Number(rawQuota)
        : Number.NaN;
  return Number.isFinite(totalQuota) && totalQuota <= 0;
}

function timestampToDate(
  value: TrialFreeChatSubscriptionView["signedUpAt"],
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  if (typeof value === "object" && typeof value._seconds === "number") {
    return new Date(value._seconds * 1000);
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  return null;
}

function readActiveTrial(sub: TrialFreeChatSubscriptionView): boolean {
  const { activeTrial } = sub;
  if (typeof activeTrial === "function") {
    // Subscription model methods read `this.usesQuota`; preserve receiver when called.
    return activeTrial.call(sub);
  }
  return Boolean(activeTrial);
}

function readQuota(sub: TrialFreeChatSubscriptionView): number {
  const raw = sub.quota;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Trial users lose the complimentary chat week once signedUpAt is more than seven days ago. */
export function isTrialPastFreeChatWeek(
  signedUpAt: TrialFreeChatSubscriptionView["signedUpAt"],
  now: Date = new Date(),
): boolean {
  const signupDate = timestampToDate(signedUpAt);
  if (!signupDate) return false;
  return now.getTime() - signupDate.getTime() > TRIAL_FREE_CHAT_DURATION_MS;
}

/** After the free week, trial users can no longer use complimentary Discuss mode. */
export function isTrialDiscussModeBlocked(
  sub: TrialFreeChatSubscriptionView | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!sub || !readActiveTrial(sub)) return false;
  return isTrialPastFreeChatWeek(sub.signedUpAt, now);
}

/** Mirrors legacy persistent chat: block trial users after the free week when out of credits. */
export function isTrialFreeChatBlocked(
  sub: TrialFreeChatSubscriptionView | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!sub || !readActiveTrial(sub)) return false;
  return isTrialPastFreeChatWeek(sub.signedUpAt, now) && readQuota(sub) <= 0;
}

/** Whether a trial user should be prevented from sending in the given composer mode. */
export function isTrialAgenticChatSendBlocked(
  sub: TrialFreeChatSubscriptionView | null | undefined,
  mode: "chat" | "write",
  now: Date = new Date(),
): boolean {
  if (!sub || !readActiveTrial(sub)) return false;
  if (isTrialFreeChatBlocked(sub, now)) return true;
  if (mode === "chat" && isTrialDiscussModeBlocked(sub, now)) return true;
  if (mode === "write" && readQuota(sub) <= 0) return true;
  return false;
}

export function shouldSkipBillingForAgenticChatMode(
  sub: TrialFreeChatSubscriptionView | null | undefined,
  mode: "chat" | "write",
  now: Date = new Date(),
): boolean {
  if (mode !== "chat") return false;
  if (!sub || !readActiveTrial(sub)) return true;
  return !isTrialPastFreeChatWeek(sub.signedUpAt, now);
}

export function subscriptionViewFromClientJSON(
  subscriptionJSON: {
    activeTrial?: boolean;
    quota?: number;
    data?: { quota?: number; signedUpAt?: Date | string | null };
  } | null | undefined,
): TrialFreeChatSubscriptionView {
  if (!subscriptionJSON) return {};
  return {
    activeTrial: Boolean(subscriptionJSON.activeTrial),
    signedUpAt: subscriptionJSON.data?.signedUpAt ?? null,
    quota:
      typeof subscriptionJSON.quota === "number"
        ? subscriptionJSON.quota
        : subscriptionJSON.data?.quota,
  };
}

export function getTrialFreeChatExpiredSignedUpAt(now: Date = new Date()): Date {
  return new Date(now.getTime() - TRIAL_FREE_CHAT_EXPIRED_SIGNED_UP_AT_OFFSET_MS);
}
